import {
  ActionIcon,
  Box,
  createStyles,
  Group,
  Input,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import React from 'react';

import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  infoIconButton: {
    color: theme.colors.gray[5],
    '&:hover': {
      color: theme.colors.gray[7],
    },
  },
  urlPreview: {
    // Nucleus theme.spacing.* are full lengths (e.g. "8px"); do not append `px`.
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[2]}`,
  },
  urlPreviewText: {
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    lineHeight: 1.5,
  },
}));

export interface McpUrlParam {
  key: string;
  label: string;
  description: string | null;
  /** Enum choices when non-null/non-empty; aligns with GraphQL `[StrictString!]`. */
  options: ReadonlyArray<string> | null;
}

interface McpUrlParamFieldsProps {
  urlParams: Array<McpUrlParam>;
  urlParamValues: Record<string, string>;
  onChange: (key: string, value: string) => void;
  serverUrlTemplate?: string | null;
  disabled?: boolean;
}

/**
 * Renders editable fields for variable MCP server URL parameters, with a live
 * URL preview. Shared between the extension store install flow and the admin
 * configuration modal edit mode.
 */
export default function McpUrlParamFields({
  urlParams,
  urlParamValues,
  onChange,
  serverUrlTemplate,
  disabled,
}: McpUrlParamFieldsProps): JSX.Element {
  const {classes} = useStyles();
  const urlPreview = serverUrlTemplate
    ? serverUrlTemplate.replace(/\{(\w+)\}/g, (_match, key) => {
        const val = (urlParamValues[key] ?? '').trim();
        return val !== '' ? val : `{${key}}`;
      })
    : null;

  return (
    <Stack spacing="sm">
      {urlParams.map((param) => {
        const labelNode = (
          <Group spacing={4} align="center" display="inline-flex">
            <span>{param.label}</span>
            {param.description != null && param.description !== '' && (
              <Tooltip
                label={param.description}
                position="top"
                withinPortal={true}
                zIndex={ZINDEX_FLOATING_UI}
              >
                <ActionIcon
                  type="button"
                  variant="subtle"
                  size="sm"
                  className={classes.infoIconButton}
                  aria-label={`More information about ${param.label}`}
                >
                  <BenchlingIcon icon="info-circle" />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        );

        return param.options && param.options.length > 0 ? (
          <NativeSelect
            key={param.key}
            label={labelNode}
            data={[{value: '', label: 'Select…'}, ...param.options.map((o) => ({value: o, label: o}))]}
            withAsterisk={true}
            value={urlParamValues[param.key] ?? ''}
            onChange={(e) => {
              const value = e.currentTarget.value;
              onChange(param.key, value);
            }}
            disabled={disabled}
          />
        ) : (
          <TextInput
            key={param.key}
            label={labelNode}
            placeholder={param.key}
            withAsterisk={true}
            value={urlParamValues[param.key] ?? ''}
            onChange={(e) => {
              const value = e.currentTarget.value;
              onChange(param.key, value);
            }}
            disabled={disabled}
          />
        );
      })}
      {urlPreview != null && (
        <Box mt="xl">
          <Input.Label labelElement="div" mb="sm" size="sm">
            <span>AI Connector URL preview</span>
          </Input.Label>
          <Box className={classes.urlPreview}>
            <Text className={classes.urlPreviewText}>{urlPreview}</Text>
          </Box>
          <Text size="xs" c="dimmed" mt="sm">
            The AI Connector will connect to this URL.
          </Text>
        </Box>
      )}
    </Stack>
  );
}
